<template>
	<section class="saved-search-card" :class="{ empty: isEmpty, custom: isUnsaved, modified: isModified }">
		<div class="header">
			<span v-if="showIcon" class="icon" aria-hidden="true">
				<AppIcon :name="IconName.Bookmark" />
			</span>
			<div class="copy">
				<div class="title-row">
					<h2>{{ title }}</h2>
					<span v-if="isModified" class="modified-pill">
						{{ t("custom-views.modified") }}
					</span>
				</div>
				<p>{{ summary }}</p>
			</div>
			<div class="corner">
				<span v-if="savedSearchCount > 0" class="count-badge">{{ savedSearchCount }}</span>
			</div>
		</div>

		<form v-if="isNamingSearch" class="save-search-form" @submit.prevent="$emit('save')">
			<input
				:value="newSearchName"
				type="text"
				:placeholder="t('custom-views.name-placeholder')"
				:disabled="isSavingSearch"
				autofocus
				@input="updateName"
			/>
			<div class="actions">
				<Button variant="ghost" size="m" :disabled="isSavingSearch" @click="$emit('cancel-save')">
					{{ t("custom-views.cancel") }}
				</Button>
				<Button variant="primary" size="m" type="submit" :disabled="isSavingSearch || !newSearchName">
					{{
						isSavingSearch
							? t("custom-views.saving")
							: hasActiveSavedSearch
								? t("custom-views.save-new-search")
								: t("custom-views.save")
					}}
				</Button>
			</div>
		</form>

		<p v-if="saveError" class="error-message">{{ saveError }}</p>

		<div v-if="!isNamingSearch" class="actions">
			<template v-if="isModified">
				<Button
					variant="primary"
					size="m"
					class="primary-action"
					:disabled="isUpdatingSearch"
					@click="$emit('update-search')"
				>
					<span class="button-icon" aria-hidden="true">↻</span>
					{{ isUpdatingSearch ? t("custom-views.updating") : t("custom-views.update") }}
				</Button>
				<Button variant="secondary" size="m" class="secondary-action" @click="$emit('start-save')">
					<AppIcon :name="IconName.Plus" class="button-icon plus-icon" />
					{{ t("custom-views.create-new") }}
				</Button>
				<Button variant="ghost" size="m" class="reset-action" @click="$emit('cancel-changes')">
					{{ t("custom-views.cancel-changes") }}
				</Button>
			</template>
			<template v-else-if="hasActiveSavedSearch">
				<Button variant="secondary" size="m" class="secondary-action" @click="$emit('open-saved-searches')">
					{{ t("custom-views.my-searches") }}
				</Button>
				<Button variant="ghost" size="m" class="reset-action" @click="$emit('reset')">
					{{ t("custom-views.reset-filters") }}
				</Button>
			</template>
			<template v-else>
				<Button variant="primary" size="m" class="primary-action" @click="$emit('start-save')">
					<AppIcon :name="IconName.Plus" class="button-icon plus-icon" />
					{{ t("custom-views.save") }}
				</Button>
				<Button variant="secondary" size="m" class="secondary-action" @click="$emit('open-saved-searches')">
					{{ t("custom-views.my-searches") }}
				</Button>
			</template>
		</div>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"

withDefaults(
	defineProps<{
		title: string
		summary: string
		isEmpty: boolean
		isUnsaved: boolean
		isModified: boolean
		hasActiveSavedSearch: boolean
		isNamingSearch: boolean
		isSavingSearch: boolean
		isUpdatingSearch: boolean
		newSearchName: string
		saveError: string
		savedSearchCount: number
		showIcon?: boolean
	}>(),
	{ showIcon: true }
)

const emit = defineEmits<{
	"update:newSearchName": [value: string]
	reset: []
	"cancel-changes": []
	"cancel-save": []
	save: []
	"open-saved-searches": []
	"update-search": []
	"start-save": []
}>()

const { t } = useI18n()

function updateName(event: Event): void {
	const input = event.target instanceof HTMLInputElement ? event.target : null
	emit("update:newSearchName", input?.value.trim() ?? "")
}
</script>

<style scoped>
.saved-search-card {
	--search-card-icon-size: var(--size-button-lg);

	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
	padding: var(--spacing-md);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);

	.header {
		display: flex;
		align-items: flex-start;
		gap: var(--spacing-sm);
	}

	.icon {
		width: var(--search-card-icon-size);
		height: var(--search-card-icon-size);
		border-radius: var(--border-radius-full);
		background: var(--accent-highlight-light);
		color: var(--primary-green);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;

		svg {
			width: var(--size-icon-lg);
			height: var(--size-icon-lg);
			fill: currentColor;
		}
	}

	.copy {
		flex: 1;
		min-width: 0;

		.title-row {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			flex-wrap: wrap;
			width: 100%;
		}

		h2 {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		p {
			margin: var(--spacing-xs) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
			line-height: var(--line-height-normal);
		}
	}

	.corner {
		flex: 0 0 auto;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: var(--spacing-xs);
	}

	.count-badge {
		min-width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		border-radius: var(--border-radius-full);
		background: var(--neutral-100);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0 var(--spacing-xs);
	}

	.actions {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
	}

	.primary-action,
	.secondary-action {
		width: 100%;

		.button-icon {
			margin-right: var(--spacing-xs);
			font-size: var(--font-size-base);
			line-height: 1;
		}

		svg.button-icon {
			width: var(--font-size-base);
			height: var(--font-size-base);
			fill: none;
			stroke: currentColor;
			stroke-width: 1.8;
			stroke-linecap: round;
			stroke-linejoin: round;
		}

		svg.plus-icon {
			width: var(--font-size-medium);
			height: var(--font-size-medium);
			stroke-width: 2;
			stroke-linecap: square;
			transform: translateY(-1px);
		}
	}

	.reset-action {
		width: 100%;
		color: var(--neutral-600);
		font-weight: var(--font-weight-medium);
		text-decoration: underline;
		text-underline-offset: 2px;
	}

	&.empty {
		.icon {
			background: var(--neutral-100);
			color: var(--neutral-500);
		}
	}

	.modified-pill {
		border-radius: var(--border-radius-full);
		background: #fdebd2;
		color: #9a5f00;
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-bold);
		letter-spacing: 0;
		padding: calc(var(--spacing-xs) * 0.5) var(--spacing-sm);
	}

	&.custom {
		border-color: var(--neutral-200);
		background: var(--neutral-0);
	}

	.save-search-form {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		input {
			width: 100%;
			height: var(--spacing-xl);
			padding: 0 var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-full);
			font-size: var(--font-size-small);
			font-family: var(--font-family-primary);
			background: var(--neutral-0);
			color: var(--neutral-900);
			outline: none;
			box-sizing: border-box;

			&:focus {
				border-color: var(--neutral-900);
			}
		}

		.actions {
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
		}
	}

	.error-message {
		margin: 0;
		color: var(--accent-warning);
		font-size: var(--font-size-small);
	}
}
</style>
